import { SimpleGrid, Text } from "@chakra-ui/react";
import React from "react";
import useApi from "../../../api/hooks/useApi";
import { logError } from "../../../error-handling/logError";
import { sortOpportunityBySortOrder } from "../../../utils/opportunities";
import { Button } from "../../common/Button";
import { OpportunityCard } from "../../common/opportunities/OpportunityCard";
import { ManagerProfileDivider } from "../ManagerProfileDivider";
import { ManagerProfileEmptySection } from "../ManagerProfileEmptySection";
import {
  ManagerProfileEditingSection,
  useManagerProfile,
} from "../ManagerProfileProvider";
import { ManagerProfileSection } from "../ManagerProfileSection";
import { useHistory } from "react-router-dom";
import { buildRouteUrl } from "../../../utils";
import { managerDashboardRoutes } from "../../../defines";

const itemsInARow = 4;

export const Opportunities: React.FC = () => {
  const { manager, setEditingDrawer, editingDrawer, isEditing, isEditor } =
    useManagerProfile();

  const history = useHistory();
  const api = useApi();
  const [draggingIndex, setDraggingIndex] = React.useState<number | undefined>(
    -1,
  );
  const [opportunities, setOpportunities] = React.useState(
    manager?.opportunities?.sort(sortOpportunityBySortOrder) || [],
  );
  const opportunitiesLength = manager.opportunities?.length || 0;
  const hasOpportunities = opportunitiesLength > 0;
  const rearrangeIsEnabled = opportunitiesLength > 1;
  const isRearranging =
    editingDrawer === ManagerProfileEditingSection.Opportunities;

  const [visibleItems, setVisibleItems] = React.useState(itemsInARow);
  const pluralOpportunity =
    opportunitiesLength == 1 ? "opportunity" : "opportunities";

  const changePreviewOrder = async (
    targetPosition: number,
    newPosition: number,
  ) => {
    const newOpportunities = opportunities ? [...opportunities] : [];
    const elm = newOpportunities.splice(targetPosition, 1)[0];
    newOpportunities.splice(newPosition, 0, elm);

    setOpportunities(newOpportunities);
    try {
      await api.rpcRequest("sort-opportunities", {
        opportunities: newOpportunities.map((f, i) => ({
          id: f.id,
          sort_order: i,
        })),
      });
    } catch (e) {
      logError("error while sorting opportunities");
    }
  };
  const goToOpportunities = () =>
    history.push(buildRouteUrl(managerDashboardRoutes.OPPORTUNITIES));

  if (!isEditing && !hasOpportunities) return <></>;

  return (
    <>
      <ManagerProfileDivider />
      <ManagerProfileSection
        w={"100%"}
        title={"Opportunities"}
        zIndex={isRearranging ? 101 : undefined}
        position={isRearranging ? "relative" : undefined}
        titleActions={
          !isEditor ? undefined : (
            <>
              {/**
               * TODO
               * The UI is done but we will disable it.
               * To do this on the server we are going to change the way they are sorted now and there is no way to fix this.
               * we are going to enable it for the last release,
               * when we can create opportunities and everyone will be using this version. if not we have to modify too many things on the V1
               * Story #4816
               */}
              {rearrangeIsEnabled && (
                <Button
                  mr={2}
                  onClick={() =>
                    setEditingDrawer(
                      isRearranging
                        ? null
                        : ManagerProfileEditingSection.Opportunities,
                    )
                  }
                  label={
                    isRearranging ? "Exit rearrange mode" : "Rearrange order"
                  }
                  isFlat={!isRearranging}
                />
              )}
            </>
          )
        }
      >
        {!hasOpportunities && (
          <ManagerProfileEmptySection
            description={"Showcase your opportunities for investors."}
            buttonLabel={"Manage your opportunities"}
            onAction={goToOpportunities}
          />
        )}
        {((!isEditing && manager) || isEditing) && (
          <>
            {isRearranging && (
              <Text textStyle={"bodyRegular"} mb={6}>
                Drag and drop to reorder opportunities, or select the shortcut
                options from the menu. We recommend sorting opportunities with
                the status “Open” and “Coming soon” first. Draft opportunities
                are not included because they are not yet visible on your
                profile.
              </Text>
            )}

            {hasOpportunities && (
              <SimpleGrid columns={{ base: 1, md: itemsInARow }} spacing={6}>
                {opportunities.slice(0, visibleItems).map((o, i) => {
                  return (
                    <OpportunityCard
                      isEditing={isEditing}
                      isEditor={isEditor}
                      index={i}
                      onChangePosition={(newPosition) => {
                        if (
                          draggingIndex !== undefined &&
                          newPosition !== undefined
                        ) {
                          changePreviewOrder(draggingIndex, newPosition);
                        }
                      }}
                      onMoveToTop={(index) => {
                        if (index !== undefined) {
                          changePreviewOrder(index, 0);
                        }
                      }}
                      onMoveToBottom={(index) => {
                        if (index !== undefined) {
                          changePreviewOrder(index, opportunities.length - 1);
                        }
                      }}
                      setDraggingIndex={setDraggingIndex}
                      isRearranging={isRearranging}
                      managerIdOrSlug={
                        manager?.slug || String(manager?.id) || ""
                      }
                      key={"oppos-" + o.id}
                      opportunity={o}
                    />
                  );
                })}
                {opportunitiesLength && visibleItems < opportunitiesLength && (
                  <Button
                    isLarge
                    outline
                    onClick={() => {
                      setVisibleItems(opportunitiesLength);
                    }}
                  >
                    Show all {opportunitiesLength} {pluralOpportunity}
                  </Button>
                )}
              </SimpleGrid>
            )}
          </>
        )}
      </ManagerProfileSection>
    </>
  );
};
